import { css } from '@microsoft/fast-element';
import { MatchMediaStyleSheetBehavior } from '@microsoft/fast-foundation';
import { componentBaseStyles } from '@adaptive-web/adaptive-ui';
import { typeRampBaseFontSize } from '@adaptive-web/adaptive-ui/reference';
import { accentForegroundRest, neutralForegroundRest } from '@adaptive-web/adaptive-ui/migration';

import { darkModeQuery, lightModeQuery } from '../style.js';
import { typeRampStyles } from '../type-ramp.js';

export default css`
  ${componentBaseStyles}
  ${typeRampStyles}

  :host {
    padding: 0;
    scrollbar-gutter: stable;
    color: var(--text-color);
    display: block;
  }

  main {
    display: grid;
    width: 100%;
    grid-template-columns: minmax(0, 8fr);
    grid-row: 2;
    grid-column: span 3;
    padding-block-end: min(4rem, 8cqb);
  }

  section {
    grid-column: span 3;

    &:not(.hero) {
      padding-inline: var(--padding-md);
    }
  }

  .wide {
    max-width: 1920px;
    width: 100%;
    margin: 0 auto;
  }

  main > section:not(.wide) {
    width: 100%;
    max-width: var(--max-content-width);
    margin-left: auto;
    margin-right: auto;
  }

  .hero section-header .hero-content {
    width: 100%;
    max-width: var(--max-content-width);
    padding: var(--padding-md);
  }

  section-header > :where(h2) {
    color: var(--section-title-color);
  }

  section-header > :where(h2, p) {
    margin-bottom: var(--padding-md);
  }

  section + section {
    margin-block-start: var(--padding-lg);
  }

  p {
    --font-size-diff: -0.1;
  }

  site-footer {
    grid-column: span 3;
    grid-row: 3;
    max-width: 100%;
  }

  .highlighted {
    color: var(--secondary-color);
  }

  strong {
    font-variation-settings: 'wght' 700;
  }

  a {
    color: ${accentForegroundRest};

    &:visited {
      color: ${neutralForegroundRest};
    }
  }

  .our-journey {
    & h3 {
      --font-variations: 'wght' 600;
      line-height: 1.4;
    }
  }

  call-to-action {
    > h2 {
      --line-height: 1rem;
      --font-variations: 'wght' 400;
    }
  }

  .testimonials {
    site-carousel {
      margin-bottom: 32px;
    }

    & adaptive-card {
      display: block;
      background-color: var(--surface-color);
      container: card / size;
      contain: content;

      & .customer {
        display: grid;
        align-items: center;
        justify-content: flex-start;
        padding-inline: calc(var(--padding-lg) * 3);
        height: 40%;
        width: 100%;

        > a {
          text-decoration: none;
          color: var(--on-background-color);
          display: flex;
          align-self: flex-start;
          align-items: center;
          gap: var(--padding-xs);
          font-size: ${typeRampBaseFontSize};

          &:hover {
            text-decoration: underline;
          }
        }

        > .avatar {
          display: grid;
          grid-template-rows: 1fr 1fr;
          gap: var(--padding-xs) 16px;

          > .image {
            grid-row: 1 / span 2;
            display: flex;
            border: 1px solid var(--primary-color);
            border-top-left-radius: 1.25rem;
            border-top-right-radius: 4px;
            border-bottom-right-radius: 1.25rem;
            border-bottom-left-radius: 4px;
            max-height: 84px;
            max-width: 98px;
            contain: content;

            > img {
              object-fit: cover;
              aspect-ratio: 4 / 3;
              width: 100%;
            }
          }

          > b {
            grid-row: 1;
            align-self: flex-end;
          }

          > div {
            grid-row: 2;
          }
        }
      }

      & .quote {
        display: grid;
        align-items: center;
        height: 60%;
        width: 100%;
        padding-block: var(--padding-md);
        padding-inline: calc(var(--padding-lg) * 3);
        margin: 0;
        box-sizing: border-box;
        background-color: var(--background-color);
        align-content: space-evenly;
        contain: content;

        > .logo {
          display: flex;
          max-height: 80px;
          justify-self: center;
        }

        > h3 {
          display: flex;
          justify-content: center;
          margin: 0;
        }

        > blockquote {
          width: 60cqi;
          margin: 0;
          position: relative;

          > .graphic {
            font-size: 4rem;
            position: absolute;
            top: -2rem;
            left: -3.25rem;
            z-index: 0;
          }

          > .text {
            font-variation-settings: 'wght' 300;
            position: relative;
            z-index: 1;
          }
        }

      }
    }
  }

  .contact {
    scroll-margin-block-start: calc((${x => x.headerHeight} + 32) * 1px);
  }

  @media (max-width: 659px) {
    .testimonials {
      & adaptive-card {
        & .quote, & .customer {
          padding-inline: var(--padding-lg);
        }

        & .quote {
          & blockquote {
            width: 80cqi;

            > .graphic {
              left: -2rem;
            }
          }
        }
      }
    }
  }

  .principles-card-deck {
    --grid-min: 16ch;

    > simple-card {
      --card-height: 250px;

      > p {
        --font-variations: 'wght' 600;
      }
    }
  }

  .expertise-card-deck {
    grid-template-columns: repeat(2, 1fr);

    p {
      font-size: 14px;
    }
  }

  @media (min-width: 660px) {
    .hero section-header .hero-content {
      & call-to-action {
        & .hero-mobile-text {
          display: none;
        }
      }
    }
  }

  @media (max-width: 659px) {
    .principles-card-deck {
      --grid-min: 33ch;
    }

    .expertise-card-deck {
      grid-template-columns: 1fr;

      p {
        line-height: 1rlh;
      }
    }

    .testamonials {
      & adaptive-card {
        width: 90cqi;
      }
    }

    .hero section-header .hero-content {
      & call-to-action {
        & .hero-desktop-text {
          display: none;
        }
      }
    }
  }
`.withBehaviors(
  new MatchMediaStyleSheetBehavior(
    lightModeQuery,
    css`
      hero-card .highlighted {
        color: inherit;
      }
    `
  ),
  new MatchMediaStyleSheetBehavior(
    darkModeQuery,
    css`
      call-to-action {
        > h2 {
          color: var(--section-title-color);
        }
      }

      .testimonials {
        & adaptive-card {
          & .quote {
            > .logo {
              filter: brightness(0) invert(1);
            }
          }
        }
      }
    `
  )
);
